import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AiChat, AiChatEvent } from '@qtek/shared/models';
import { of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { AiChatActions } from './ai-chat.actions';
import { AiChatService } from './ai-chat.service';

@Injectable()
export class AiChatEffects {
  constructor(
    private actions$: Actions,
    private chatSvc: AiChatService
  ) {}

  private normalizeChat(raw: any): AiChat | null {
    if (!raw) return null;
    const id = raw?.id ?? raw?._id;
    if (!id) return null;
    const title = raw?.title ?? raw?.name ?? raw?.subject;
    const preview =
      typeof raw?.preview === 'string'
        ? raw.preview
        : typeof raw?.summary === 'string'
          ? raw.summary
          : typeof raw?.snippet === 'string'
            ? raw.snippet
            : undefined;
    const updatedAtSource =
      raw?.updatedAt ?? raw?._uts ?? raw?.uts ?? raw?.updated;
    let updatedAt: number | undefined;
    if (typeof updatedAtSource === 'number') {
      updatedAt = updatedAtSource;
    } else if (typeof updatedAtSource === 'string') {
      const parsed = Date.parse(updatedAtSource);
      if (!Number.isNaN(parsed)) {
        updatedAt = parsed;
      }
    }
    return { id, title, preview, updatedAt };
  }

  subscribeChats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiChatActions.subscribeChats),
      switchMap(({ payload }) =>
        this.chatSvc.subscribeChats(payload).pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(AiChatActions.unsubscribeChats),
              filter(({ mysid }) => mysid === payload.mysid)
            )
          ),
          map((msg: any) => {
            const { op, res, id, sts, mysid } = msg;
            switch (op) {
              case 'query': {
                if (!res) {
                  return AiChatActions.getChatsFailure({ error: sts });
                }
                const chats = (res as any[])
                  .map(item => this.normalizeChat(item))
                  .filter((item): item is AiChat => !!item);
                return AiChatActions.getChatsSuccess({ chats, mysid });
              }
              case 'ins': {
                const normalized =
                  this.normalizeChat(res) ??
                  ({
                    id: (id as string) ?? (res?.id as string),
                    title: res?.title,
                  } as AiChat);
                if (!normalized.updatedAt) {
                  normalized.updatedAt = Date.now();
                }
                return AiChatActions.createChatSuccess({
                  chat: normalized,
                  mysid,
                });
              }
              case 'upd': {
                const normalized =
                  this.normalizeChat(res) ??
                  ({
                    id: (res as any)?.id,
                    title: (res as any)?.title,
                  } as AiChat);
                if (!normalized.updatedAt) {
                  normalized.updatedAt = Date.now();
                }
                return AiChatActions.updateChatSuccess({ chat: normalized });
              }
              default:
                return { type: '[AI Chat] Unknown chats WS op' };
            }
          }),
          catchError(error => of(AiChatActions.getChatsFailure({ error })))
        )
      )
    )
  );

  getChats$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AiChatActions.getChats),
        tap(({ payload }) => this.chatSvc.queryChats(payload))
      ),
    { dispatch: false }
  );

  createChat$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AiChatActions.createChat),
        tap(({ payload }) => {
          this.chatSvc.createChat(payload);
        })
      ),
    { dispatch: false }
  );

  updateChat$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AiChatActions.updateChat),
        tap(({ payload }) => this.chatSvc.updateChat(payload))
      ),
    { dispatch: false }
  );

  subscribeChatEvents$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AiChatActions.subscribeChatEvents),
      switchMap(({ payload }) =>
        this.chatSvc.subscribeChatEvents(payload).pipe(
          takeUntil(
            this.actions$.pipe(
              ofType(AiChatActions.unsubscribeChatEvents),
              filter(({ mysid }) => mysid === payload.mysid)
            )
          ),
          map((msg: any) => {
            const { op, res, sts, mysid } = msg;
            switch (op) {
              case 'query':
                return res
                  ? AiChatActions.getChatEventsSuccess({
                      events: res as AiChatEvent[],
                      mysid,
                    })
                  : AiChatActions.getChatEventsFailure({ error: sts });
              case 'ins':
                return AiChatActions.getChatEventsSuccess({
                  events: [res as AiChatEvent],
                  mysid,
                });
              default:
                return { type: '[AI Chat] Unknown events WS op' };
            }
          }),
          catchError(error => of(AiChatActions.getChatEventsFailure({ error })))
        )
      )
    )
  );

  getChatEvents$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AiChatActions.getChatEvents),
        tap(({ payload }) => this.chatSvc.queryChatEvents(payload))
      ),
    { dispatch: false }
  );

  sendMessage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AiChatActions.sendMessage),
        tap(({ payload }) => {
          this.chatSvc.sendEvent(payload);
        })
      ),
    { dispatch: false }
  );
}
